import { type FC, useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { Pulldown, ValidationErrorMessage } from "@legacy_user_frontend/components/ui_parts";
import {
  type CassetteProps,
  type LineListResponseProps,
  type StationListResponseProps,
  type StationGroupResponseProps,
} from "@legacy_user_frontend/components/pages/mypage/profiles/profiles_edit/src/profiles_edit_props";
import { arrayToObjects } from "@legacy_user_frontend/components/pages/mypage/profiles/profiles_edit/src/modules";
import { DeletableCassette } from "@legacy_user_frontend/components/pages/mypage/profiles/ui_parts/deletable_cassette";
import {
  errorMessageOuter,
  lineAndStationOuter,
  stationList as stationListStyle,
} from "@legacy_user_frontend/components/pages/mypage/profiles/profiles_edit/src/styles.css";
import { axios } from "@root/libs/axios";
import { sendErrorLog } from "@root/shared/utils/sendErrorLog";

export const StationCassette: FC<CassetteProps> = ({
  cassetteIndex,
  lineList,
  stationList,
  remove,
  fetchStationsListPath,
  fetchStationsGroupPath,
  fetchLinesListPath,
}) => {
  const cassetteNumber = 1 + cassetteIndex;
  const cassetteFormName = `form_user_frontend_user_update[stations_attributes][${cassetteIndex}]`;
  const { formState, setValue, resetField, watch, clearErrors } = useFormContext();
  const validateErrors = formState.errors.form_user_frontend_user_update?.stations_attributes;
  const stationErrors = validateErrors && validateErrors[`${cassetteIndex}`];

  // 都道府県の変更を駅・路線に反映する
  const watchedPrefectureId = watch("form_user_frontend_user_update[prefecture_id]");
  const [selectedLineList, setSelectedLineList] = useState(lineList);
  useEffect(() => {
    if (!watchedPrefectureId) {
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get<LineListResponseProps>(fetchLinesListPath, {
          params: {
            prefecture: watchedPrefectureId,
          },
          caseConversion: "none",
        });
        // HTTP 200 で返るアプリケーション側エラー（{ status: false }）を catch に流す
        if (data.status === false) {
          throw new Error(data.message || "路線の取得に失敗しました");
        }
        setSelectedLineList(arrayToObjects(data));
      } catch (error: unknown) {
        sendErrorLog(error instanceof Error ? error : new Error("路線の取得に失敗しました"));
      }
    })();
  }, [fetchLinesListPath, watchedPrefectureId]);

  // 路線の変更を駅に反映する
  const watchedLineId = watch(`${cassetteFormName}[line_id]`);
  const watchedStationId = watch(`${cassetteFormName}[station_id]`);
  const [selectedStationList, setSelectedStationList] = useState(stationList);
  useEffect(() => {
    if (!watchedLineId) {
      setSelectedStationList([]);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get<StationListResponseProps>(fetchStationsListPath, {
          params: {
            line: watchedLineId,
          },
          caseConversion: "none",
        });
        // HTTP 200 で返るアプリケーション側エラー（{ status: false }）を catch に流す
        if (data.status === false) {
          throw new Error(data.message || "駅の取得に失敗しました");
        }
        setSelectedStationList(arrayToObjects(data));
      } catch (error: unknown) {
        sendErrorLog(error instanceof Error ? error : new Error("駅の取得に失敗しました"));
      }
    })();
  }, [fetchStationsListPath, cassetteFormName, resetField, watchedLineId]);
  useEffect(() => {
    const isSelectable = selectedStationList.filter(({ value }) => {
      if (value === watchedStationId) {
        return true;
      }
      return false;
    });
    if (watchedStationId && isSelectable) {
      setValue(`${cassetteFormName}[station_id]`, watchedStationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStationList]);

  // 駅と路線を選択したら、最寄駅リストを表示する
  const [fetchedStationsGroup, setFetchedStationsGroup] = useState<string[]>([]);
  useEffect(() => {
    if (!watchedStationId) {
      setFetchedStationsGroup([]);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get<StationGroupResponseProps>(fetchStationsGroupPath, {
          params: {
            station: watchedStationId,
          },
          caseConversion: "none",
        });
        // HTTP 200 で返るアプリケーション側エラー（{ status: false }）を catch に流す
        if (data.status === false) {
          throw new Error(data.message || "最寄駅の取得に失敗しました");
        }
        setFetchedStationsGroup(data);
      } catch (error: unknown) {
        setFetchedStationsGroup([]);
        sendErrorLog(error instanceof Error ? error : new Error("最寄駅の取得に失敗しました"));
      }
    })();
  }, [fetchStationsGroupPath, watchedStationId]);

  // 路線をの場合は駅プルダウンを非活性にする
  const [isStationEntered, setIsStationEntered] = useState(true);
  useEffect(() => {
    if (watchedLineId) {
      setIsStationEntered(true);
    } else {
      setIsStationEntered(false);
    }
  }, [watchedLineId]);

  // 削除処理
  const handleOnClick = () => {
    remove(cassetteIndex);
    clearErrors("form_user_frontend_user_update[stations]");
  };

  return (
    <DeletableCassette heading={`最寄り駅${cassetteNumber}`} handleOnClick={handleOnClick}>
      <>
        <div
          className={lineAndStationOuter({
            // 駅と路線が選択済み（最寄駅リストがある）の場合はtrue
            isSelected: !!(fetchedStationsGroup.length > 0),
          })}
        >
          <Pulldown
            name={`${cassetteFormName}[line_id]`}
            placeholder="路線を選択"
            list={selectedLineList}
          />
          <Pulldown
            name={`${cassetteFormName}[station_id]`}
            requiredMessage="最寄り駅を選択してください"
            placeholder="駅を選択"
            list={selectedStationList}
            isDisabled={!isStationEntered}
          />
        </div>
        {fetchedStationsGroup.length > 0 && (
          <ul className={stationListStyle}>
            {fetchedStationsGroup.map((station: string) => (
              <li key={station}>{station}</li>
            ))}
          </ul>
        )}
        {stationErrors && (
          <div className={errorMessageOuter}>
            {stationErrors.line_id?.message && (
              <ValidationErrorMessage message={stationErrors.line_id.message} />
            )}
            {stationErrors.station_id?.message && (
              <ValidationErrorMessage message={stationErrors.station_id.message} />
            )}
          </div>
        )}
      </>
    </DeletableCassette>
  );
};
